import Fuse from "fuse.js";
import { isString } from "lodash-es";
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router";
import { fromISO8601 } from "../../../utils/iso8601";

import type { ISO8601 } from "../../../utils/iso8601";
import type { SearchAttributes } from "./utils";

export function useMomentQueryFilterFunction<T extends SearchAttributes>(): (moments: T[]) => T[] {
  const [searchParams] = useSearchParams();

  const filters = useMemo(
    () => ({
      programs: searchParams.getAll("program"),
      statuses: searchParams.getAll("status"),
      channels: searchParams.getAll("channel"),
      events: searchParams.getAll("event"),
      segments: searchParams.getAll("segment"),
      q: searchParams.get("q"),
      start: searchParams.get("start"),
      end: searchParams.get("end"),
    }),
    [searchParams],
  );

  const fn = useCallback(
    (moments: T[]) => {
      let filteredMoments = moments;
      const { programs, statuses, channels, events, segments, q, start, end } = filters;

      if (programs.length > 0) {
        filteredMoments = filteredMoments.filter((m) => programs.includes(m.program_id || ""));
      }

      if (statuses.length > 0) {
        filteredMoments = filteredMoments.filter((m) => statuses.includes(m.status || ""));
      }

      if (channels.length > 0) {
        filteredMoments = filteredMoments.filter(
          (m) =>
            m.channel &&
            channels.map((x) => (x === "email" ? "work_email" : x)).includes(m.channel),
        );
      }

      if (events.length > 0) {
        filteredMoments = filteredMoments.filter((m) =>
          events.includes(m.schedule?.event_key || ""),
        );
      }

      if (segments.length > 0) {
        filteredMoments = filteredMoments.filter(
          (m) => m.segment === null || segments.includes(m.segment?.id || ""),
        );
      }

      if (isString(q) && q.trim().length > 0) {
        const fuse = new Fuse(filteredMoments, { keys: ["title"], useExtendedSearch: true });
        filteredMoments = fuse.search(q).map((r) => r.item);
      }

      if (isString(start)) {
        filteredMoments = filteredMoments.filter((m) =>
          // oxlint-disable-next-line typescript/no-unsafe-type-assertion
          m.when ? fromISO8601(m.when) >= fromISO8601(start as ISO8601) : true,
        );
      }

      if (isString(end)) {
        filteredMoments = filteredMoments.filter((m) =>
          // oxlint-disable-next-line typescript/no-unsafe-type-assertion
          m.when ? fromISO8601(m.when) <= fromISO8601(end as ISO8601) : true,
        );
      }

      return filteredMoments;
    },
    [filters],
  );

  return fn;
}
